<template>
  <div class="merged-knowledge-list">
    <a-card title="合并后的知识列表" size="small">
      <div v-if="mergedKnowledge && mergedKnowledge.merge_summary" style="margin-bottom: 16px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
        <div style="font-size: 12px; color: #666;">
          <div>查询到的知识：{{ getTotalCount(mergedKnowledge.merge_summary.queried_count) }} 条</div>
          <div>配置中的知识：{{ getTotalCount(mergedKnowledge.merge_summary.config_count) }} 条</div>
          <div>合并后的知识：<strong>{{ getTotalCount(mergedKnowledge.merge_summary.merged_count) }} 条</strong></div>
        </div>
      </div>
      <a-list v-if="groupedKnowledge.length > 0" size="small">
        <template v-for="group in groupedKnowledge" :key="group.type">
          <a-list-item>
            <a-typography-title :level="5" style="margin: 0">{{ group.typeName }}</a-typography-title>
          </a-list-item>
          <a-list-item 
            v-for="item in group.items" 
            :key="item.id"
            :class="{ 'selected': selectedKnowledgeId === item.id }"
            style="padding-left: 24px"
          >
            <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
              <span 
                class="knowledge-title"
                :style="{ 
                  backgroundColor: item.backgroundColor,
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  flex: 1
                }"
                @click="handleClick(item.id)"
              >{{ item.title }}</span>
              <div style="display: flex; align-items: center; gap: 8px; margin-left: 8px;">
                <a-tag :color="item.source === 'config' ? 'blue' : 'green'" size="small">
                  {{ item.source === 'config' ? '配置' : '查询' }}
                </a-tag>
                <a 
                  href="#" 
                  @click.stop="handleOpenKnowledge(item.id)"
                  style="font-size: 12px;"
                >查看</a>
              </div>
            </div>
          </a-list-item>
        </template>
      </a-list>
      <div v-else style="color: #999; text-align: center; padding: 16px">
        暂无合并后的知识
      </div>
    </a-card>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { KNOWLEDGE_COLORS } from '@/constants/knowledgeColors';

const TYPE_NAMES = {
  prompt: '提示词',
  dataflow: '数据流',
  pin_info: '针脚信息',
  case: '典型案例',
  principle: '系统原理'
};

export default {
  name: 'MergedKnowledgeList',
  props: {
    mergedKnowledge: {
      type: Object,
      default: null
    },
    configKnowledge: {
      type: Object,
      default: null
    },
    selectedKnowledgeId: {
      type: Number,
      default: null
    }
  },
  emits: ['knowledge-click'],
  setup(props, { emit }) {
    const router = useRouter();
    const groupedKnowledge = computed(() => {
      if (!props.mergedKnowledge || !props.mergedKnowledge.knowledge_details) {
        return [];
      }

      const groups = {};
      const knowledgeDetails = props.mergedKnowledge.knowledge_details;
      const mergedIds = props.mergedKnowledge.merged_knowledge_ids || {};
      const configIds = props.configKnowledge?.knowledge_details || {};

      // 构建配置知识ID集合（用于判断来源）
      const configIdSets = {};
      for (const [type, items] of Object.entries(configIds)) {
        if (Array.isArray(items)) {
          configIdSets[type] = new Set(items.map(k => k.id));
        }
      }

      // 按类型分组
      for (const [typeCode, knowledgeList] of Object.entries(knowledgeDetails)) {
        if (!Array.isArray(knowledgeList) || knowledgeList.length === 0) {
          continue;
        }

        if (!groups[typeCode]) {
          groups[typeCode] = {
            type: typeCode,
            typeName: TYPE_NAMES[typeCode] || typeCode,
            items: []
          };
        }

        knowledgeList.forEach(k => {
          if (k && k.id) {
            // 判断知识来源
            const source = (configIdSets[typeCode] && configIdSets[typeCode].has(k.id)) ? 'config' : 'queried';
            // 获取对应类型的背景色
            const backgroundColor = KNOWLEDGE_COLORS[typeCode] || '#f0f0f0';
            groups[typeCode].items.push({
              ...k,
              type_code: typeCode,
              source: source,
              backgroundColor: backgroundColor
            });
          }
        });
      }

      return Object.values(groups);
    });

    const getTotalCount = (countObj) => {
      if (!countObj) return 0;
      return (countObj.prompt || 0) + 
             (countObj.dataflow || 0) + 
             (countObj.pin_info || 0) + 
             (countObj.case || 0) + 
             (countObj.principle || 0);
    };

    const handleClick = (knowledgeId) => {
      emit('knowledge-click', knowledgeId);
    };

    const handleOpenKnowledge = (knowledgeId) => {
      const routeData = router.resolve({ name: 'knowledge-edit', params: { id: knowledgeId } });
      window.open(routeData.href, '_blank');
    };

    return {
      groupedKnowledge,
      getTotalCount,
      handleClick,
      handleOpenKnowledge
    };
  }
};
</script>

<style scoped>
.merged-knowledge-list {
  margin-bottom: 16px;
}

.selected {
  background-color: #e6f7ff;
}

.knowledge-title {
  transition: opacity 0.2s;
}

.knowledge-title:hover {
  opacity: 0.8;
}
</style>

